import { Grid, Stack, Typography } from "@mui/material";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { MultiSelect } from "components/common/inputs/multiSelect";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useState } from "react";
import { StrategyDashboardEditingData } from "src/store/api/generatedApi";
import { SelectBox } from "../Selectbox";
import {
  ALL_EDITING_METRICS,
  EditingMetric,
  getMetricTranslationKey,
} from "./constants";
import { EditingMetricsPlot } from "./EditingMetricsPlot";
import { EditingPlot } from "./EditingPlot";
import { EditingRecipesTable } from "./EditingRecipesTable";

type EditingTabProps = Readonly<{
  data?: StrategyDashboardEditingData;
}>;

export const EditingTab = ({ data }: EditingTabProps) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();

  const availableProductGroups = data?.available_product_groups ?? [];
  const series = data?.series ?? [];

  const defaultProductGroup = availableProductGroups.includes("All")
    ? "All"
    : (availableProductGroups[0] ?? null);

  const [selectedProductGroup, setSelectedProductGroup] =
    useStringQueryParamState("productGroup", defaultProductGroup);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [selectedMetrics, setSelectedMetrics] =
    useLiteralListQueryParamState<EditingMetric>({
      paramName: "editingMetrics",
      permitted: ALL_EDITING_METRICS,
      localStorageKey: `${tenant}.strategyDashboard.editing.selectedMetrics`,
      defaultValue: [
        "avg_num_edits_per_heat",
        "avg_num_materials_edited_per_heat",
      ],
    });

  return (
    <Stack sx={{ mt: 2 }}>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <DateRangeSelector />
      </Stack>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid>
          <SelectBox
            availableOptions={availableProductGroups}
            selectedOption={selectedProductGroup}
            onOptionChange={setSelectedProductGroup}
            id="product-group-select"
            labelId="product-group-label"
            label={t("productGroup")}
          />
        </Grid>
      </Grid>
      <Stack sx={{ mt: 2 }}>
        {selectedProductGroup && series.length > 0 ? (
          <>
            <EditingPlot
              series={series}
              selectedProductGroup={selectedProductGroup}
              setSelectedDate={setSelectedDate}
            />
            {selectedDate && selectedProductGroup ? (
              <EditingRecipesTable
                selectedDate={selectedDate}
                selectedProductGroup={selectedProductGroup}
              />
            ) : null}
            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid>
                <MultiSelect
                  sx={{ mt: 0 }}
                  options={Array.from(ALL_EDITING_METRICS)}
                  getOptionId={(metric) => metric}
                  getOptionLabel={(metric) =>
                    t(getMetricTranslationKey(metric))
                  }
                  values={selectedMetrics}
                  setValues={setSelectedMetrics}
                  small
                  selectAll
                  labelAbove
                  label={t("editingMetrics")}
                />
              </Grid>
            </Grid>
            {selectedMetrics.length > 0 && (
              <EditingMetricsPlot
                series={series}
                selectedProductGroup={selectedProductGroup}
                selectedMetrics={selectedMetrics}
              />
            )}
          </>
        ) : (
          <Typography sx={{ mt: 2 }}>
            {availableProductGroups.length === 0
              ? t("noProductGroupsAvailable")
              : series.length === 0
                ? t("noEditingDataAvailableForDateRange")
                : t("selectProductGroupToViewData")}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
